// © Microsoft Corporation. All rights reserved.
import { AppType, ExperienceTypes, RiverConfigType, UrlSearchParamsHelper, WebWorkerMessageId, environment, logger, ocidMapping, registerCommandMessageHandler, scnMapping, sendMessage, webWorkerCache } from "../wwCommon";
import { FetchOneServiceMyFeedFailed, OneServiceFeedResponseEmpty, RiverConfigIsNull } from "../AppErrors";
import { aadTokenQsKey, aadTokenQsVal, getOneServiceFeedCallHeaders, shouldMakeEnterpriseRequest, shouldUseEnterpriseDomain } from "./OneServiceRequest";
import { addEndPerfMarker, addStartPerfMarker, OneServiceResponseSchema, tryReformatGuid, trySetSearchParam } from "./Common";
import { oneServiceQueryStringKeys } from "./OneServiceConstants";
import { get } from "lodash-es";
import { experienceConfigMap, getExperienceTypeConfig } from "../wwResolver";
/** Path inside One Service to retrieve content when feed id is not present */
const feedLayoutUrlPath = "MSN/FeedLayout";
/** Path inside One Service to retrieve content when feed id is not present */
const myFeedUrlPath = "MSN/Feed/me";
/** Path inside One Service to retrieve content when feed id is present */
const xFeedUrlPath = "MSN/Feed";
let feedDisplaySetting;
/**
 * Registers command message handler for feed display setting. Used only in Anaheim
 */
export function registerFeedDisplaySettingListener() {
    registerCommandMessageHandler(WebWorkerMessageId.FeedDisplaySetting, (setting) => {
        webWorkerCache.set(WebWorkerMessageId.FeedDisplaySetting, setting);
    });
}
/**
 * Registers OneServiceFeed tasks
 */
export function registerOneServiceFeedTasks() {
    if (!environment.isPrefetchEnabled) {
        return;
    }
    registerCommandMessageHandler(WebWorkerMessageId.UpdateMyFeed, updateOneServiceFeeds);
    subscribeToFeedConfigs();
}
/**
 * Registers OneServiceFeed tasks for Edge Chromium
 */
export function registerOneServiceFeedTasksForEdgeChromium() {
    if (!environment.isPrefetchEnabled) {
        return;
    }
    registerCommandMessageHandler(WebWorkerMessageId.UpdateMyFeed, updateOneServiceFeeds);
    const messagesToWait = [WebWorkerMessageId.FeedDisplaySetting];
    if (environment.isEnterpriseCompliantNTP) {
        messagesToWait.push(WebWorkerMessageId.AuthToken);
    }
    const subscription = webWorkerCache.subscribeMultiple(messagesToWait, (data) => {
        subscription.unsubscribe();
        subscribeToFeedConfigs(data);
    });
}
/** Subscribes to feed configs and fetches feed when configs are available  */
function subscribeToFeedConfigs(data) {
    if (data && Array.isArray(data) && data.length) {
        // First subscription is for feed display setting
        feedDisplaySetting = data[0];
    }
    const riverConfigsToWait = [RiverConfigType.MyFeed];
    const stripeConfigsToWait = [RiverConfigType.StripeMyFeed];
    if (environment.isEnterpriseCompliantNTP) {
        riverConfigsToWait.push(ExperienceTypes.EntryPointEdgeNext);
        stripeConfigsToWait.push(ExperienceTypes.EntryPointEdgeNext);
    }
    const myFeedConfigSubscription = experienceConfigMap.subscribeMultiple(riverConfigsToWait, () => {
        myFeedConfigSubscription.unsubscribe();
        fetchRiverFeed(environment.cacheBypassValue);
    });
    const stripeConfigSubscription = experienceConfigMap.subscribeMultiple(stripeConfigsToWait, () => {
        stripeConfigSubscription.unsubscribe();
        fetchStripeFeed(environment.cacheBypassValue);
    });
}
/**
 * Makes fetch call for OneService my feed and marks cache state as fetching item
 * @param cacheBypassValue - value used (if not null) to bypass http cache on request
 */
export function fetchRiverFeed(cacheBypassValue) {
    if (!canFetchFeed()) {
        return;
    }
    const riverConfig = getExperienceTypeConfig(RiverConfigType.MyFeed);
    // If configs have await and we still didn't get river config, don't make feed call in web worker.
    if (!riverConfig) {
        logger.error(Object.assign(Object.assign({}, RiverConfigIsNull), { message: `fetchOneService-${WebWorkerMessageId.MyFeed}: River config is null when it is expecting to be not null.` }));
        return;
    }
    fetchOneServiceFeed(riverConfig, WebWorkerMessageId.MyFeed, cacheBypassValue);
}
/**
 * Take action to re-fetch one service my feed.
 * @param cacheBypassValue - value used (if not null) to bypass http cache on request
 */
export function updateOneServiceFeeds(cacheBypassValue) {
    webWorkerCache.delete(WebWorkerMessageId.MyFeed);
    webWorkerCache.delete(WebWorkerMessageId.StripeMyFeed);
    // Enterprise NTP auth token might have expired when these calls are made,
    // to be safe, avoid making API calls
    if (environment.isEnterpriseCompliantNTP) {
        return;
    }
    fetchRiverFeed(cacheBypassValue);
    fetchStripeFeed(cacheBypassValue);
}
/**
 * Fetches top stories data for stripe view
 * @param cacheBypassValue - value used (if not null) to bypass http cache on request
 */
function fetchStripeFeed(cacheBypassValue) {
    if (!canFetchFeed()) {
        return;
    }
    const stripeRiverConfig = getExperienceTypeConfig(RiverConfigType.StripeMyFeed);
    if (!stripeRiverConfig) {
        // Stripe view is not enabled for all app-types, don't log an error if the config is not found
        return;
    }
    fetchOneServiceFeed(stripeRiverConfig, WebWorkerMessageId.StripeMyFeed, cacheBypassValue);
}
/**
 * Makes fetch call for OneService my feed and marks cache state as fetching item
 * @param riverConfig - the river configuration to use for generating the url
 * @param cacheBypassValue - value used (if not null) to bypass http cache on request
 */
function fetchOneServiceFeed(riverConfig, messageId, cacheBypassValue) {
    addStartPerfMarker(messageId);
    const url = getRequestURL(riverConfig, cacheBypassValue);
    if (!url) {
        onFetchComplete(messageId);
        return;
    }
    const init = {
        credentials: "include",
        method: "GET",
        headers: getOneServiceFeedCallHeaders()
    };
    let sessionId;
    const feedUrl = url.toString();
    const tabbedInfopaneQueryString = (url.searchParams.get(oneServiceQueryStringKeys.feedLayoutRequestType) || "").toLowerCase();
    const isTabbedInfopaneResponse = tabbedInfopaneQueryString === "tabriver" || tabbedInfopaneQueryString === "tabs";
    const responseSchema = url.searchParams.get(oneServiceQueryStringKeys.responseSchema);
    return fetch(feedUrl, init).then(response => {
        if (!response.ok) {
            throw `OneServiceFeed call for ${messageId} failed with status code ${response.status}`;
        }
        if (response.headers) {
            sessionId = response.headers.get("ddd-session-id");
        }
        // Handle No-Content response
        if (response.status === 204) {
            // set response as per responseSchema to use the right property to check and log error in the then block below
            if (responseSchema === OneServiceResponseSchema.CardView) {
                return isTabbedInfopaneResponse ? [{ subCards: [] }] : { subCards: [] };
            }
            else {
                return { feedItems: [] };
            }
        }
        return response.json();
    }).then((data) => {
        let feedItems;
        if (responseSchema === OneServiceResponseSchema.CardView) {
            feedItems = isTabbedInfopaneResponse ? (data[0] && data[0].subCards) : data.subCards;
        }
        else {
            feedItems = data.feedItems;
        }
        if (!Array.isArray(feedItems) || !feedItems.length) {
            logger.error(Object.assign(Object.assign({}, OneServiceFeedResponseEmpty), { message: "OneService response empty", pb: Object.assign(Object.assign({}, OneServiceFeedResponseEmpty.pb), { fetchNetworkResponse: {
                        activityId: environment.activityId,
                        requestUrl: feedUrl
                    } }) }));
        }
        onFetchComplete(messageId, {
            feedData: data,
            sessionId,
            isTabbedInfopaneResponse,
            responseSchema
        });
    }).catch(reason => {
        logger.error(Object.assign(Object.assign({}, FetchOneServiceMyFeedFailed), { message: "OneService MyFeed fetch failed", pb: Object.assign(Object.assign({}, FetchOneServiceMyFeedFailed.pb), { customMessage: `Error: ${reason}. Url: [${feedUrl}]` }) }));
        onFetchComplete(messageId);
    });
}
/**
 * Marks cache state complete for OneService my feed. Responds to any pending messages. And triggers the content preview fetch.
 * @param response - The response
 */
function onFetchComplete(messageId, response = null) {
    addEndPerfMarker(messageId);
    webWorkerCache.set(messageId, { payload: response });
    sendMessage({ id: messageId, fetched: true, payload: response });
}
/**
 * Returns the feed url.
 * @param cacheBypassValue - value used (if > 0) to bypass http cache on request
 * @returns - The url with required query params
 */
function getRequestURL(riverConfig, cacheBypassValue) {
    const isEnterpriseRequest = shouldMakeEnterpriseRequest();
    const initialRequest = get(riverConfig, "properties.cardProviderConfig.initialRequest");
    const { feedId, feedLayoutRequestType } = initialRequest || {};
    let requestPath;
    if (feedLayoutRequestType) {
        requestPath = feedLayoutUrlPath;
    }
    else if (feedId) {
        requestPath = xFeedUrlPath;
    }
    else {
        requestPath = myFeedUrlPath;
    }
    const urlBase = shouldUseEnterpriseDomain() ? environment.enterpriseServiceUrlBase : environment.feedServiceUrlBase;
    const feedUrl = new URL(requestPath, urlBase);
    const urlSearchParams = new UrlSearchParamsHelper(urlBase.search);
    setDefaultSearchParams(urlSearchParams);
    // When tabbed infopane is active, we use "/FeedLayout" which doesn't accept the queryType param
    if (!feedLayoutRequestType) {
        urlSearchParams.set(oneServiceQueryStringKeys.queryType, "myfeed");
        if (environment.latitude && environment.longitude) {
            urlSearchParams.set(oneServiceQueryStringKeys.location, `${environment.latitude}|${environment.longitude}`);
        }
    }
    else {
        urlSearchParams.set("t", feedLayoutRequestType);
    }
    if (cacheBypassValue > 0) {
        urlSearchParams.set("cbypass", cacheBypassValue.toString());
    }
    if (isEnterpriseRequest) {
        urlSearchParams.set(aadTokenQsKey, aadTokenQsVal);
    }
    if (!initialRequest) {
        feedUrl.search = urlSearchParams.toString();
        return feedUrl;
    }
    if (initialRequest.infopaneItemCount) {
        urlSearchParams.set(oneServiceQueryStringKeys.infopaneCount, initialRequest.infopaneItemCount);
        if (initialRequest.complexInfopaneEnabled === false) {
            urlSearchParams.set(oneServiceQueryStringKeys.complexInfoPaneEnabled, "false");
        }
    }
    // Update response schema query string when faltview is disabled via config
    if (initialRequest.disableFlatViewResponse) {
        urlSearchParams.set(oneServiceQueryStringKeys.responseSchema, OneServiceResponseSchema.CardView);
    }
    if (initialRequest.useDelta) {
        urlSearchParams.set(oneServiceQueryStringKeys.delta, "true");
    }
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.ids, feedId);
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.ocid, initialRequest.ocid);
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.contentType, initialRequest.contentType);
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.top, initialRequest.count);
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.timeOut, initialRequest.timeoutMs);
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.query, initialRequest.query);
    feedUrl.search = urlSearchParams.toString();
    return feedUrl;
}
/**
 * Gets the feed url with default search parameters
 * @param urlSearchParamsHelper search params
 */
function setDefaultSearchParams(urlSearchParamsHelper) {
    urlSearchParamsHelper.set(oneServiceQueryStringKeys.apiKey, environment.oneServiceApiKey)
        .set(oneServiceQueryStringKeys.ocid, ocidMapping[environment.pagetype] || "Peregrine")
        .set(oneServiceQueryStringKeys.market, environment.locale)
        .set(oneServiceQueryStringKeys.responseSchema, OneServiceResponseSchema.FlatView)
        .set(oneServiceQueryStringKeys.wrapOData, "false")
        .set(oneServiceQueryStringKeys.disableTypeSerialization, "true")
        .set(oneServiceQueryStringKeys.timeOut, "1000") // Default to 1000
        .set(oneServiceQueryStringKeys.top, "30"); // Default to 30
    if (!environment.sendFeedCallActivityIdInHeader) {
        trySetSearchParam(urlSearchParamsHelper, oneServiceQueryStringKeys.activityId, tryReformatGuid(environment.activityId));
    }
    trySetSearchParam(urlSearchParamsHelper, oneServiceQueryStringKeys.user, environment.userId);
    trySetSearchParam(urlSearchParamsHelper, oneServiceQueryStringKeys.fdhead, environment.currentFlights);
    if (environment.signedIn) {
        urlSearchParamsHelper.set(oneServiceQueryStringKeys.signInCookieName, scnMapping[environment.pagetype] || "ANON");
    }
}
/** Checks and returns a boolean indicating if the feed call can be made */
function canFetchFeed() {
    if (environment.apptype !== AppType.EdgeChromium) {
        return true;
    }
    return feedDisplaySetting === "always";
}
//# sourceMappingURL=OneServiceFeed.js.map